import { type FC, useState } from 'react';

import type {
  AssetsApprovalSchema,
  AssetsApprovalSchemaNullable,
  AssetsAssetSchemaTypedWithIdNullable,
  AssetsShareElasticSchemaWithIdNullable,
  AssetsShareSchema,
} from '@iconik-io/iconik-api/src/services/assets';
import {
  OBJECT_TYPE_ASSETS,
  type ObjectType,
} from '@iconik-io/iconik-api/src/types/object_types';
import type { UserRoles } from '@iconik-io/shared-lib/src/utils/roles';

import ShareSettings from '../../components/share_settings/share_settings';
import type { ShareSettingsPartForm } from '../../components/share_settings/types';
import { useAppSelector } from '../../store/react_redux_typed';
import Button from '../../ui/button/button';
import Collapsible from '../../ui/collapsible/collapsible';
import CheckboxFieldWrapper from '../../ui/forms/field_wrappers/checkbox/checkbox';
import Form from '../../ui/forms/form';

import styles from './asset_review.module.css';

type Props = {
  asset: AssetsAssetSchemaTypedWithIdNullable;
  reviewRequest?: AssetsApprovalSchemaNullable;
  dateTimeFormat: string;
  editShare: (
    assetTypes: ObjectType,
    assetId: string,
    shareId: string,
    shareSettings: AssetsShareSchema,
  ) => void;
  editReviewRequest: (assetId: string, data: AssetsApprovalSchema) => void;
  onDeleteApproval: () => void;
  canDelete?: boolean;
  showMinNumber: boolean;
  share?: AssetsShareElasticSchemaWithIdNullable;
  roles: UserRoles;
};

const ReviewShareForm: FC<Props> = (props) => {
  const { dateTimeFormat, share, canDelete, roles, asset, reviewRequest } =
    props;

  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  const allowSetSearchUsersFromShare = useAppSelector((state) =>
    Boolean(state.settings.merged.search_users_from_share),
  );

  const handleSettingsChange = (data: {
    share: ShareSettingsPartForm;
    approval?: { min_number: number };
  }) => {
    if (!share) {
      return;
    }

    if (reviewRequest && reviewRequest.share_id) {
      const shareData = {
        ...share,
        // converting fields from undefined | null to undefined§
        date_created: share.date_created ?? undefined,
        has_password: share.has_password ?? undefined,
        object_id: share.object_id ?? undefined,
        object_type: share.object_type ?? undefined,
        owner_id: share.owner_id ?? undefined,
        system_domain_id: share.system_domain_id ?? undefined,
        allow_upload: share.allow_upload ?? undefined,
        allow_user_search_for_mentions:
          share.allow_user_search_for_mentions ?? undefined,
        allow_sync: share.allow_sync ?? undefined,
        allow_view_versions: share.allow_view_versions ?? undefined,
        expires: share.expires ?? undefined,
        schedule_status: share.schedule_status ?? undefined,
        review_experience_public_beta: true,
        ...data.share,
        allow_download: Boolean(data.share.allow_download),
        allow_download_proxy: Boolean(data.share.allow_download_proxy),
        allow_comments: Boolean(data.share.allow_comments),
        allow_approving_comments: Boolean(data.share.allow_approving_comments),
        allow_custom_actions: Boolean(data.share.allow_custom_actions),
        allow_view_transcriptions: Boolean(
          data.share.allow_view_transcriptions,
        ),
        automatic_approval_share: share.automatic_approval_share ?? undefined,
        population_status: share.population_status ?? undefined,
        starts: data.share.starts || null,
      };

      const initialMinNumber = props.reviewRequest?.min_number;

      if (
        typeof initialMinNumber === 'number' &&
        initialMinNumber > 1 &&
        data.approval?.min_number === undefined
      ) {
        data = {
          ...data,
          approval: {
            ...data.approval,
            min_number: initialMinNumber,
          },
        };
      }

      if (data.approval?.min_number) {
        const externals = reviewRequest.externals?.length || 0;
        const users = reviewRequest.users?.length || 0;
        props.editReviewRequest(asset.id, {
          min_number: externals + users,
        });
      } else {
        props.editReviewRequest(asset.id, { min_number: 1 });
      }

      props.editShare(
        OBJECT_TYPE_ASSETS,
        asset.id,
        reviewRequest.share_id,
        shareData,
      );
    }
  };

  if (!share) {
    return null;
  }

  const defaultFieldProps = {
    labelClassName: styles.switch_setting_label,
    controlClassName: styles.switch_setting_control,
    className: styles.switch_setting,
  };

  return (
    <Form onChange={handleSettingsChange}>
      <br />
      <Collapsible
        triggerClassName={styles.settings_trigger}
        trigger={showSettings ? 'Hide settings' : 'Show settings'}
        onTriggerClick={() => setShowSettings(!showSettings)}
        showContent={showSettings}
      >
        {props.showMinNumber && (
          <CheckboxFieldWrapper
            name="approval.min_number"
            label="Require approval from all reviewers"
            value={(props.reviewRequest?.min_number ?? 1) > 1}
          />
        )}
        <ShareSettings
          isPanel
          objectType={OBJECT_TYPE_ASSETS}
          fieldProps={{
            starts: {
              value: share.starts ?? undefined,
            },
            expires: {
              type: 'date',
              fieldProps: {
                value: share.expires ?? undefined,
                disabled: true,
                dateFormat: dateTimeFormat,
              },
            },
            metadata_views: {
              value: share.metadata_views ?? [],
            },
            allow_download: {
              ...defaultFieldProps,
              value: share.allow_download || false,
            },
            allow_upload: {
              ...defaultFieldProps,
              value: share.allow_upload || false,
            },
            upload_storage_id: {
              ...defaultFieldProps,
              value: share.upload_storage_id ?? undefined,
            },
            allow_comments: {
              ...defaultFieldProps,
              value: share.allow_comments || false,
            },
            allow_approving_comments: {
              ...defaultFieldProps,
              value: share.allow_approving_comments || false,
            },
            allow_custom_actions: {
              ...defaultFieldProps,
              value: share.allow_custom_actions || false,
            },
            allow_view_versions: {
              ...defaultFieldProps,
              value: share.allow_view_versions ?? false,
            },
            allow_user_search_for_mentions: {
              ...defaultFieldProps,
              value: share.allow_user_search_for_mentions ?? false,
            },
            allow_view_transcriptions: {
              ...defaultFieldProps,
              value: share.allow_view_transcriptions ?? false,
            },
            allow_download_proxy: {
              ...defaultFieldProps,
              value: share.allow_download_proxy ?? false,
            },
            title: {
              value: share?.title || '',
              labelWidth: '27%',
            },
            show_watermark: {
              ...defaultFieldProps,
              value: share.show_watermark || undefined,
            },
            allow: {
              ...defaultFieldProps,
            },
            allow_setting_approve_status: {
              ...defaultFieldProps,
              value: share.allow_setting_approve_status ?? false,
            },
            allow_sync: {
              ...defaultFieldProps,
              value: share.allow_sync ?? true,
            },
          }}
          allowViewVersionsHidden
          showAdvanced={showAdvanced}
          onAdvancedClick={() => setShowAdvanced(!showAdvanced)}
          roles={roles}
          allowSetSearchUsersFromShare={allowSetSearchUsersFromShare}
          hideAllowSettingApprovalStatusField
          shouldCreatePlaylistShare={false}
        />
        <div className={styles.buttons}>
          {canDelete && (
            <Button
              onClick={props.onDeleteApproval}
              iconName="deletion_delete"
              variant="danger"
            >
              Delete Approval
            </Button>
          )}
        </div>
      </Collapsible>
    </Form>
  );
};

export default ReviewShareForm;
